import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  NavigationPageItemDto,
  SortColumnDto,
  TableDto,
  WidgetDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  FullscreenWidgetModal,
  setAgGridLicense,
} from '@asset-metrix/portal-ui';
import { AG_GRID_GROUP_AUTO_COLUMN_ID } from '@asset-metrix/portal-ui';
import { useDisclosure } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';

import { fetchTableFilterCustomization } from '../../api/portalGateway/tableFilterCustomization';
import { AG_GRID_LICENSE_KEY } from '../../config';
import { useFullscreenInstanceIds } from '../../hooks/useFullscreenInstanceIds';
import { useStore } from '../../store';
import { invalidateTable } from '../../utils/queryCacheHelper';
import { BulkDeleteConfirmation } from '../bulkDeleteConfirmation/BulkDeleteConfirmation';
import { useBulkDeleteConfirmationProps } from '../bulkDeleteConfirmation/useBulkDeleteConfirmationProps';
import { CreateRowModal } from '../createRow/CreateRowModal';
import { useCreateRowModalProperties } from '../createRow/useCreateRowModalProperties';
import { DocUploadModal } from '../docUpload/DocUploadModal';
import { InfoIconModal } from '../infoicon/InfoIconModal';
import { InitFundOperationModal } from '../initFundOperation/InitFundOperationModal';
import { useInitFundOperationModalProperties } from '../initFundOperation/useInitFundOperationModalProperties';
import { TableCustomFilter } from '../tablecustomization/TableCustomFilter';
import { TableCustomization } from '../tablecustomization/TableCustomization';
import { UpdateTableRowModal } from '../updateTableRow/UpdateTableRowModal';
import { useUpdateTableRowModalProperties } from '../updateTableRow/useUpdateTableRowModalProperties';
import { useWorkflowModal } from '../workflowAction/useWorkflowModal';
import { WorkflowActionModal } from '../workflowAction/WorkflowActionModal';
import { ZipUploadModal } from '../zipUpload/ZipUploadModal';

import { TableContent, TableContentProps } from './TableContent';
import { CustomColumnWidths, useCustomization } from './useCustomization';

if (AG_GRID_LICENSE_KEY != null) {
  setAgGridLicense(AG_GRID_LICENSE_KEY);
}

export const PAGINATION_SIZES = [12, 25, 50, 100];

export type TableProps = Pick<
  WidgetDto,
  'allowOpenInFullscreen' | 'collapsible' | 'id' | 'title' | 'infoIcon'
> & {
  entity: TableDto;
  instanceId: string;
} & Pick<NavigationPageItemDto, 'pageId'>;

export const Table = ({
  allowOpenInFullscreen = false,
  collapsible = false,
  id: widgetId,
  infoIcon,
  title: widgetTitle,
  entity,
  pageId,
  instanceId,
}: TableProps) => {
  const [currentSortItems, setCurrentSortItems] = useState<SortColumnDto[]>(
    entity.config.sortColumns
  );

  const [customColumnWidths, setCustomColumnWidths] =
    useState<CustomColumnWidths>({});

  const setFilterLoaded = useStore((state) => state.setFilterLoaded);

  const {
    fullscreenInstanceStateId,
    widgetFullscreenInstanceStateId,
    cloneFullscreenInstanceState,
  } = useFullscreenInstanceIds({
    pageFilterInstanceId: instanceId,
    widgetFilterInstanceId: widgetId,
  });

  const [infoIconOpened, { close: closeInfoIcon, open: openInfoIcon }] =
    useDisclosure();

  const [zipUploadOpened, { close: closeZipUpload, open: openZipUpload }] =
    useDisclosure();

  const [docUploadOpened, { close: closeDocUpload, open: openDocUpload }] =
    useDisclosure();

  const { updateTableRowModalProps, open: openUpdateTableRow } =
    useUpdateTableRowModalProperties(
      pageId,
      widgetId,
      entity.config,
      instanceId
    );

  const { initFundOperationModalProps, open: openInitFundOperation } =
    useInitFundOperationModalProperties(pageId, widgetId, entity.config);

  const { createRowModalProps, open: openCreateRowModal } =
    // @todo: if we need to support modals in fullscreen, this needs a switch: instanceId / fullscreenInstanceId
    useCreateRowModalProperties(pageId, widgetId, entity.config, instanceId);

  const { workflowActionModalProps, open: openWorkflowModal } =
    useWorkflowModal(pageId, widgetId, entity.config);

  const [fullscreenOpened, { close: closeFullscreen, open: openFullscreen }] =
    useDisclosure(false, {
      onOpen: () => cloneFullscreenInstanceState(),
    });

  const [
    customizationOpened,
    { close: closeCustomization, open: openCustomization },
  ] = useDisclosure();

  const [
    customFilterOpened,
    { close: closeCustomFilter, open: openCustomFilter },
  ] = useDisclosure();

  const {
    open: openBulkDeleteConfirmation,
    props: bulkDeleteConfirmationProps,
  } = useBulkDeleteConfirmationProps();

  const customFilterColumns = useMemo(
    () =>
      entity.config.columns.filter(
        (columnConfig) => columnConfig.customFilter != null
      ),
    [entity.config.columns]
  );

  const { data: storedCustomFilterData } = useQuery({
    queryKey: ['tableFilterCustomization', { widgetId }],
    queryFn: fetchTableFilterCustomization,
    enabled: customFilterColumns.length > 0,
  });

  const activeCustomFilterIds = useMemo(
    () =>
      storedCustomFilterData?.data.columns
        .map((c) => c.columnId)
        .filter((c) => customFilterColumns.find((i) => i.id === c)) ?? [],
    [customFilterColumns, storedCustomFilterData?.data.columns]
  );

  // Set table filters loaded if no filters configured
  // Check for custom-filters if they are present, and then only setFilterLoaded,
  //  if no-custom-filters are present.
  //  or custom-filters are present and none of them are active.
  useEffect(() => {
    const noFilters = !entity.config.filters?.length;
    const isWidgetIdValid = widgetId != null;
    if (
      noFilters &&
      isWidgetIdValid &&
      (customFilterColumns.length === 0 ||
        (customFilterColumns.length > 0 && activeCustomFilterIds.length === 0))
    ) {
      setFilterLoaded(widgetId);
    }
  }, [
    widgetId,
    entity,
    setFilterLoaded,
    customFilterColumns.length,
    activeCustomFilterIds.length,
  ]);

  const {
    customizedColumns,
    customizationData,
    isCustomized,
    isCustomizable,
    isColumnInitiallyHidden,
  } = useCustomization(widgetId, entity.config, customColumnWidths);

  const onColumnWidthChanged = useCallback(
    (columnWidth: CustomColumnWidths) => {
      setCustomColumnWidths({ ...customColumnWidths, ...columnWidth });
    },
    [customColumnWidths]
  );

  const tableContentProps = useMemo<TableContentProps>(
    () => ({
      activeCustomFilterIds,
      collapsible,
      customFilterColumns,
      customizedColumns,
      defaultPageSize: entity.config.pageSize,
      entity,
      groupColumnWidth: customColumnWidths[AG_GRID_GROUP_AUTO_COLUMN_ID]?.width,
      infoIcon,
      isColumnInitiallyHidden,
      isCustomizable,
      isCustomized,
      onBulkUpdateTableRowModalOpened: openUpdateTableRow,
      onCreateRowModalOpened: openCreateRowModal,
      onInitFundOperationModalOpened: openInitFundOperation,
      onModalOpened: openWorkflowModal,
      openCustomFilter,
      openCustomization,
      openDeleteConfirmation: openBulkDeleteConfirmation,
      openDocUpload,
      openInfoIcon,
      openZipUpload,
      pageFilterInstanceId: instanceId,
      pageId,
      title: widgetTitle,
      widgetFilterInstanceId: widgetId,
      widgetId,
    }),
    [
      activeCustomFilterIds,
      collapsible,
      customColumnWidths,
      customFilterColumns,
      customizedColumns,
      entity,
      infoIcon,
      instanceId,
      isColumnInitiallyHidden,
      isCustomizable,
      isCustomized,
      openBulkDeleteConfirmation,
      openCreateRowModal,
      openCustomFilter,
      openCustomization,
      openDocUpload,
      openInfoIcon,
      openInitFundOperation,
      openUpdateTableRow,
      openWorkflowModal,
      openZipUpload,
      pageId,
      widgetId,
      widgetTitle,
    ]
  );

  return (
    <>
      {ContentRenderer({
        ...tableContentProps,
        enableScrollElementToTop: true,
        onColumnWidthChanged: onColumnWidthChanged,
        onSortItemsChanged: setCurrentSortItems,
        openFullscreen: allowOpenInFullscreen ? openFullscreen : undefined,
        sortItems: currentSortItems,
      })}
      {allowOpenInFullscreen &&
        FullscreenContentRenderer({
          ...tableContentProps,
          closeFullscreen,
          defaultPageSize: entity.config.isGroupedTable
            ? entity.config.pageSize
            : 50,
          defaultSortItems: currentSortItems,
          fullscreenOpened,
          isFullScreenMode: true,
          pageFilterInstanceId: fullscreenInstanceStateId,
          widgetFilterInstanceId: widgetFullscreenInstanceStateId,
        })}
      {infoIcon && (
        <InfoIconModal
          onClose={closeInfoIcon}
          opened={infoIconOpened}
          headline={infoIcon?.headline}
          text={infoIcon?.text}
        />
      )}
      {entity.config.enableDocumentZipUpload && (
        <ZipUploadModal
          contentId={widgetId}
          onClose={closeZipUpload}
          onUploadSuccess={async () => {
            await invalidateTable(entity.config.cacheKey, pageId, widgetId);
          }}
          opened={zipUploadOpened}
        />
      )}
      {entity.config.uploadJsonSchema && docUploadOpened && (
        <DocUploadModal
          contentId={widgetId}
          onClose={closeDocUpload}
          jsonSchema={entity.config.uploadJsonSchema}
          onUploadSuccess={async () => {
            await invalidateTable(entity.config.cacheKey, pageId, widgetId);
          }}
          documentMetaDataExtraction={entity.config.documentMetaDataExtraction}
        />
      )}
      {updateTableRowModalProps && entity.config.updateJsonSchema && (
        <UpdateTableRowModal
          {...updateTableRowModalProps}
          jsonSchema={entity.config.updateJsonSchema}
        />
      )}
      {initFundOperationModalProps &&
        entity.config.initFundOperationJsonSchema && (
          <InitFundOperationModal
            {...initFundOperationModalProps}
            jsonSchema={entity.config.initFundOperationJsonSchema}
          />
        )}
      {createRowModalProps && <CreateRowModal {...createRowModalProps} />}
      {workflowActionModalProps && (
        <WorkflowActionModal {...workflowActionModalProps} />
      )}
      {isCustomizable && (
        <TableCustomization
          customizationData={customizationData ?? null}
          modalOpened={customizationOpened}
          onClose={closeCustomization}
          tableConfig={entity.config}
          widgetId={widgetId}
          isCustomized={isCustomized}
        />
      )}
      {customFilterColumns.length > 0 && (
        <TableCustomFilter
          activeCustomFilterIds={activeCustomFilterIds}
          customFilterColumns={customFilterColumns}
          modalOpened={customFilterOpened}
          onClose={closeCustomFilter}
          widgetId={widgetId}
          widgetFilterInstanceIds={[widgetId, widgetFullscreenInstanceStateId]}
        />
      )}
      <BulkDeleteConfirmation {...bulkDeleteConfirmationProps} />
    </>
  );
};

const ContentRenderer = (tableProps: TableContentProps) => {
  return <TableContent {...tableProps} />;
};

type FullscreenContentRendererProps = TableContentProps & {
  fullscreenOpened: boolean;
  closeFullscreen: () => void;
};

const FullscreenContentRenderer = ({
  fullscreenOpened,
  ...tableProps
}: FullscreenContentRendererProps) => {
  return (
    <FullscreenWidgetModal
      opened={fullscreenOpened}
      onClose={tableProps.closeFullscreen}
    >
      <TableContent {...tableProps} />
    </FullscreenWidgetModal>
  );
};
